import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Calculator, Pause, Play, Plus, Send } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Section,
  Select,
  Spinner,
  Switch,
  type Tone,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { reviewTone } from '../components/ContentReview';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { EmailCopyDraft } from './ai/drafts';
import type {
  AudienceEstimate,
  CampaignDetail,
  CampaignReport,
  CampaignRow,
  EmailTemplateRow,
  SegmentRow,
} from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Pazarlama → Kampanyalar (F6-06, ADR-087): taslak (şablon, konu, kitle, A/B), alıcı tahmini, gönder/zamanla
 * (MFA + Idempotency-Key), ilerleme, durdurma/sürdürme ve rapor. Gönderimi worker yapar; ekran 5 sn'de bir yenilenir.
 */

type Status = CampaignRow['status'];
const statusTone: Record<Status, Tone> = {
  draft: 'neutral',
  scheduled: 'slate',
  sending: 'pine',
  testing: 'amber',
  sent: 'moss',
  paused: 'rose',
  cancelled: 'slate',
};
const LIVE: Status[] = ['scheduled', 'sending', 'testing'];
const SKIP_KEYS = [
  'suppressed',
  'no_consent',
  'iys_not_registered',
  'iys_refused',
  'invalid_address',
  'staging_allowlist',
] as const;
const PERCENTS = [10, 20, 30, 40, 50];
const WAIT_HOURS = [1, 2, 4, 8, 12, 24, 48];

/** `datetime-local` alanı için yerel saat (saniyesiz). */
function toLocalInput(v: string | Date | null): string {
  if (!v) return '';
  const d = new Date(v);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const pct = (part: number, whole: number) => (whole ? Math.round((part / whole) * 1000) / 10 : 0);

// ---------------------------------------------------------------------------- Liste

export function CampaignsPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const list = useQuery({
    queryKey: ['email-campaigns'],
    queryFn: () => api.get<CampaignRow[]>('/v1/admin/email-campaigns'),
    refetchInterval: (q) => (q.state.data?.some((c) => LIVE.includes(c.status)) ? 5000 : false),
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const rows = (list.data ?? []).filter((c) => c.storeId === storeId);
  return (
    <PageFrame
      helpKey="page.campaigns"
      actions={
        can('marketing.campaign.write') &&
        storeId && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={() => void navigate({ to: '/e-posta-kampanyalari/$id', params: { id: 'yeni' } })}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.campaigns.create')}
            </Button>
            <HelpTip k="campaigns.create" />
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <div className="space-y-6">
          <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.campaigns.lead')}</p>
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          {!list.data ? (
            <Spinner label={t('common.loading')} />
          ) : !rows.length ? (
            <EmptyState
              title={t('admin.campaigns.emptyTitle')}
              description={t('admin.campaigns.emptyLead')}
            />
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={t('admin.campaigns.listLabel')}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.campaigns.colName')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.campaigns.colStatus')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.campaigns.colSent')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.campaigns.colClicked')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.campaigns.colWhen')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id} className="border-t border-line align-top">
                      <td className="px-3 py-2">
                        <Link
                          to="/e-posta-kampanyalari/$id"
                          params={{ id: c.id }}
                          className="font-medium text-pine hover:underline"
                        >
                          {c.name}
                        </Link>
                        {c.templateName && <p className="text-ink-3">{c.templateName}</p>}
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={statusTone[c.status]}>{t(`admin.campaigns.statuses.${c.status}`)}</Badge>
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {c.progress ? `${c.progress.sent} / ${c.progress.total}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {c.progress?.sent ? `%${pct(c.progress.clicked, c.progress.sent)}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-end text-ink-2">
                        {formatDate(c.finishedAt ?? c.startedAt ?? c.scheduledAt ?? c.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------- Düzenleyici ve rapor

interface Draft {
  name: string;
  templateId: string;
  subject: string;
  preheader: string;
  segmentIds: string[];
  subscribers: boolean;
  ab: boolean;
  subjectB: string;
  percent: number;
  waitHours: number;
  ratePerMinute: number;
}

const emptyDraft = (): Draft => ({
  name: '',
  templateId: '',
  subject: '',
  preheader: '',
  segmentIds: [],
  subscribers: true,
  ab: false,
  subjectB: '',
  percent: 20,
  waitHours: 4,
  ratePerMinute: 300,
});

const draftOf = (c: CampaignDetail): Draft => ({
  name: c.name,
  templateId: c.templateId ?? '',
  subject: c.subject ?? '',
  preheader: c.preheader ?? '',
  segmentIds: c.audience.segmentIds,
  subscribers: c.audience.subscribers,
  ab: !!c.abTest,
  subjectB: c.subjectB ?? '',
  percent: c.abTest?.percent ?? 20,
  waitHours: c.abTest?.waitHours ?? 4,
  ratePerMinute: c.ratePerMinute,
});

export function CampaignEditorPage() {
  const { id } = useParams({ from: '/app/e-posta-kampanyalari/$id' });
  const isNew = id === 'yeni';
  const t = useT();
  const q = useQuery({
    queryKey: ['email-campaign', id],
    queryFn: () => api.get<CampaignDetail>(`/v1/admin/email-campaigns/${id}`),
    enabled: !isNew,
    refetchInterval: (s) => (s.state.data && LIVE.includes(s.state.data.status) ? 5000 : false),
  });
  if (!isNew && !q.data) return <Spinner label={t('common.loading')} />;
  return <CampaignEditor key={q.data?.id ?? 'yeni'} campaign={q.data ?? null} />;
}

function CampaignEditor({ campaign }: { campaign: CampaignDetail | null }) {
  const t = useT();
  const { formatDate } = useI18n();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('marketing.campaign.write');
  const canSend = can('marketing.campaign.send');
  const { stores, storeId: chosenStore, setStoreId } = useStoreChoice();
  const storeId = campaign?.storeId ?? chosenStore;
  const editable = canWrite && (!campaign || campaign.status === 'draft');
  const templates = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => api.get<EmailTemplateRow[]>('/v1/admin/email-templates'),
  });
  const segments = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get<SegmentRow[]>('/v1/admin/segments'),
    enabled: can('marketing.audience.read'),
  });
  const [draft, setDraft] = useState<Draft>(() => (campaign ? draftOf(campaign) : emptyDraft()));
  useEffect(() => {
    if (campaign) setDraft(draftOf(campaign));
  }, [campaign]);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const dirty = !campaign || JSON.stringify(draft) !== JSON.stringify(draftOf(campaign));
  const storeTemplates = (templates.data ?? []).filter((x) => x.storeId === storeId);
  const chosenTpl = storeTemplates.find((x) => x.id === draft.templateId);
  const complete =
    draft.name.trim().length >= 2 &&
    !!draft.templateId &&
    (draft.segmentIds.length > 0 || draft.subscribers) &&
    (!draft.ab || !!draft.subjectB.trim());

  const body = () => ({
    name: draft.name,
    templateId: draft.templateId,
    subject: draft.subject.trim() || null,
    preheader: draft.preheader.trim() || null,
    subjectB: draft.ab ? draft.subjectB.trim() || null : null,
    abTest: draft.ab ? { percent: draft.percent, waitHours: draft.waitHours } : null,
    audience: { segmentIds: draft.segmentIds, subscribers: draft.subscribers },
    ratePerMinute: draft.ratePerMinute,
  });
  const save = useApiMutation(
    () =>
      campaign
        ? api.patch<CampaignDetail>(`/v1/admin/email-campaigns/${campaign.id}`, {
            ...body(),
            version: campaign.version,
          })
        : api.post<CampaignDetail>('/v1/admin/email-campaigns', { ...body(), storeId }),
    {
      invalidate: [['email-campaigns'], ...(campaign ? [['email-campaign', campaign.id]] : [])],
      success: t('admin.campaigns.saved'),
      onSuccess: (r) => {
        if (!campaign) void navigate({ to: '/e-posta-kampanyalari/$id', params: { id: r.id } });
      },
    },
  );
  const [estimate, setEstimate] = useState<AudienceEstimate | null>(null);
  const estimateM = useApiMutation(
    () =>
      api.post<AudienceEstimate>('/v1/admin/email-campaigns/audience', {
        storeId,
        audience: { segmentIds: draft.segmentIds, subscribers: draft.subscribers },
      }),
    { onSuccess: (r) => setEstimate(r) },
  );
  useEffect(() => setEstimate(null), [draft.segmentIds, draft.subscribers]);
  const remove = useApiMutation(() => api.del(`/v1/admin/email-campaigns/${campaign!.id}`), {
    invalidate: [['email-campaigns']],
    success: t('admin.campaigns.deleted'),
    onSuccess: () => void navigate({ to: '/e-posta-kampanyalari' }),
  });
  const cancel = useApiMutation(
    () =>
      api.post<CampaignDetail>(`/v1/admin/email-campaigns/${campaign!.id}/cancel`, {
        version: campaign!.version,
      }),
    {
      invalidate: [['email-campaigns'], ['email-campaign', campaign?.id]],
      success: t('admin.campaigns.cancelled'),
    },
  );
  const resumeIdem = useRef(new IdempotencyKey());
  const resume = useApiMutation(
    () => {
      const b = { version: campaign!.version };
      return api.post<CampaignDetail>(`/v1/admin/email-campaigns/${campaign!.id}/resume`, b, {
        idempotencyKey: resumeIdem.current.for(JSON.stringify(b)),
      });
    },
    {
      invalidate: [['email-campaigns'], ['email-campaign', campaign?.id]],
      success: t('admin.campaigns.resumed'),
    },
  );
  const fe = (f: string) => fieldError(save.error, f);

  return (
    <PageFrame
      helpKey="page.campaign_editor"
      title={campaign ? campaign.name : t('admin.campaigns.create')}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {editable && (
            <>
              <Button
                variant={campaign ? 'secondary' : 'primary'}
                onClick={() => save.mutate(undefined)}
                disabled={!dirty || !complete || !storeId || save.isPending}
              >
                {campaign ? t('admin.campaigns.save') : t('admin.campaigns.createConfirm')}
              </Button>
              <HelpTip k="campaigns.save" />
            </>
          )}
          {campaign?.status === 'draft' && canSend && <SendButton campaign={campaign} dirty={dirty} />}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/e-posta-kampanyalari" className="text-pine hover:underline">
          {t('admin.campaigns.back')}
        </Link>
        {campaign && (
          <Badge tone={statusTone[campaign.status]}>{t(`admin.campaigns.statuses.${campaign.status}`)}</Badge>
        )}
        {campaign && editable && dirty && <Badge tone="amber">{t('admin.campaigns.unsaved')}</Badge>}
      </div>
      {!campaign && <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />}

      {campaign && campaign.status !== 'draft' && (
        <StatusSection
          campaign={campaign}
          canSend={canSend}
          onCancel={() => cancel.mutateAsync(undefined)}
          onResume={() => resume.mutate(undefined)}
          busy={cancel.isPending || resume.isPending}
        />
      )}

      <fieldset disabled={!editable} className="space-y-8">
        <legend className="sr-only">{t('admin.campaigns.editor')}</legend>
        <Section title={t('admin.campaigns.content')} className="border-t-0 pt-0">
          <div className="grid max-w-2xl gap-4">
            <Field
              label={t('admin.campaigns.name')}
              description={t('admin.campaigns.nameLead')}
              help={<HelpTip k="campaigns.name" />}
              error={fe('name')}
              required
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.name}
                  maxLength={120}
                  onChange={(e) => set({ name: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.campaigns.template')}
              help={<HelpTip k="campaigns.template" />}
              error={fe('templateId')}
              required
            >
              {(p) => (
                <Select {...p} value={draft.templateId} onChange={(e) => set({ templateId: e.target.value })}>
                  <option value="">{t('admin.campaigns.chooseTemplate')}</option>
                  {storeTemplates.map((x) => (
                    <option key={x.id} value={x.id}>
                      {`${x.name} (${x.locale.toUpperCase()})`}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {chosenTpl && (
              <p className="flex flex-wrap items-center gap-2 text-sm">
                {(chosenTpl.reviewRequired || chosenTpl.reviewStatus !== 'not_required') && (
                  <Badge tone={reviewTone[chosenTpl.reviewStatus]}>
                    {t(`admin.pages.reviews.${chosenTpl.reviewStatus}`)}
                  </Badge>
                )}
                <Link
                  to="/e-posta-sablonlari/$id"
                  params={{ id: chosenTpl.id }}
                  className="text-pine hover:underline"
                >
                  {t('admin.campaigns.openTemplate')}
                </Link>
              </p>
            )}
            {!storeTemplates.length && templates.data && (
              <p className="text-sm text-ink-2">
                {t('admin.campaigns.noTemplates')}{' '}
                <Link to="/e-posta-sablonlari" className="text-pine hover:underline">
                  {t('admin.nav.emailTemplates')}
                </Link>
              </p>
            )}
            {can('marketing.campaign.write') && (
              <EmailCopyDraft
                locale={chosenTpl?.locale ?? 'tr'}
                onApply={(v) =>
                  set({
                    ...(v.subject !== undefined ? { subject: v.subject } : {}),
                    ...(v.preheader !== undefined ? { preheader: v.preheader } : {}),
                    ...(v.subjectB !== undefined ? { subjectB: v.subjectB, ab: true } : {}),
                  })
                }
              />
            )}
            <Field
              label={t('admin.campaigns.subject')}
              optionalLabel={t('common.optional')}
              description={t('admin.campaigns.subjectLead', { subject: chosenTpl?.subject ?? '—' })}
              help={<HelpTip k="campaigns.subject" />}
              error={fe('subject')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.subject}
                  maxLength={200}
                  onChange={(e) => set({ subject: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.campaigns.preheader')}
              optionalLabel={t('common.optional')}
              description={t('admin.campaigns.preheaderLead')}
              help={<HelpTip k="campaigns.preheader" />}
              error={fe('preheader')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.preheader}
                  maxLength={200}
                  onChange={(e) => set({ preheader: e.target.value })}
                />
              )}
            </Field>
          </div>
        </Section>

        <Section
          title={t('admin.campaigns.audience')}
          description={t('admin.campaigns.audienceLead')}
          action={<HelpTip k="campaigns.audience" />}
        >
          <div className="grid max-w-2xl gap-3">
            {fe('audience') && (
              <p role="alert" className="text-sm font-medium text-rose">
                {fe('audience')}
              </p>
            )}
            <fieldset className="space-y-1.5">
              <legend className="mb-1 text-sm font-medium">{t('admin.campaigns.segments')}</legend>
              {!(segments.data ?? []).length ? (
                <p className="text-sm text-ink-3">{t('admin.campaigns.noSegments')}</p>
              ) : (
                (segments.data ?? []).map((sg) => (
                  <label key={sg.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={draft.segmentIds.includes(sg.id)}
                      onChange={(e) =>
                        set({
                          segmentIds: e.target.checked
                            ? [...draft.segmentIds, sg.id]
                            : draft.segmentIds.filter((x) => x !== sg.id),
                        })
                      }
                    />
                    {sg.name}
                    <span className="text-ink-3">
                      {t('admin.campaigns.segmentCount', { count: sg.memberCount })}
                    </span>
                  </label>
                ))
              )}
            </fieldset>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={draft.subscribers}
                onChange={(e) => set({ subscribers: e.target.checked })}
              />
              {t('admin.campaigns.subscribers')}
              <HelpTip k="campaigns.subscribers" />
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={() => estimateM.mutate(undefined)}
                disabled={!storeId || (!draft.segmentIds.length && !draft.subscribers) || estimateM.isPending}
              >
                <Calculator className="h-4 w-4" aria-hidden />
                {t('admin.campaigns.estimate')}
              </Button>
              <HelpTip k="campaigns.estimate" />
            </div>
            {estimate && <EstimateView estimate={estimate} />}
          </div>
        </Section>

        <Section
          title={t('admin.campaigns.abTitle')}
          description={t('admin.campaigns.abLead')}
          action={<HelpTip k="campaigns.ab" />}
        >
          <div className="grid max-w-2xl gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="campaign-ab"
                checked={draft.ab}
                onCheckedChange={(v) => set({ ab: v })}
                disabled={!editable}
              />
              <label htmlFor="campaign-ab" className="text-sm font-medium">
                {t('admin.campaigns.abEnable')}
              </label>
            </div>
            {draft.ab && (
              <>
                <Field
                  label={t('admin.campaigns.subjectB')}
                  help={<HelpTip k="campaigns.subject_b" />}
                  error={fe('subjectB')}
                  required
                >
                  {(p) => (
                    <Input
                      {...p}
                      value={draft.subjectB}
                      maxLength={200}
                      onChange={(e) => set({ subjectB: e.target.value })}
                    />
                  )}
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={t('admin.campaigns.abPercent')} help={<HelpTip k="campaigns.ab_percent" />}>
                    {(p) => (
                      <Select
                        {...p}
                        value={draft.percent}
                        onChange={(e) => set({ percent: Number(e.target.value) })}
                      >
                        {PERCENTS.map((x) => (
                          <option key={x} value={x}>
                            {t('admin.campaigns.percentOption', { pct: x })}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  <Field label={t('admin.campaigns.abWait')} help={<HelpTip k="campaigns.ab_wait" />}>
                    {(p) => (
                      <Select
                        {...p}
                        value={draft.waitHours}
                        onChange={(e) => set({ waitHours: Number(e.target.value) })}
                      >
                        {WAIT_HOURS.map((x) => (
                          <option key={x} value={x}>
                            {t('admin.campaigns.hoursOption', { count: x })}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </div>
              </>
            )}
          </div>
        </Section>

        <Section title={t('admin.campaigns.delivery')} description={t('admin.campaigns.deliveryLead')}>
          <Field
            label={t('admin.campaigns.rate')}
            help={<HelpTip k="campaigns.rate" />}
            error={fe('ratePerMinute')}
            className="max-w-xs"
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                min={10}
                max={3000}
                value={draft.ratePerMinute}
                onChange={(e) =>
                  set({ ratePerMinute: Math.max(10, Math.min(3000, Number(e.target.value) || 300)) })
                }
              />
            )}
          </Field>
        </Section>
      </fieldset>

      {campaign?.startedAt && <ReportSection campaign={campaign} />}

      {campaign &&
        canWrite &&
        (campaign.status === 'draft' || (campaign.status === 'cancelled' && !campaign.startedAt)) && (
          <Section title={t('admin.campaigns.dangerZone')}>
            <div className="flex items-center gap-2">
              <ConfirmButton
                message={t('admin.campaigns.deleteConfirm', { name: campaign.name })}
                onConfirm={() => remove.mutateAsync(undefined)}
              >
                {t('common.delete')}
              </ConfirmButton>
              <HelpTip k="campaigns.delete" />
            </div>
          </Section>
        )}
      {campaign && (
        <p className="text-sm text-ink-3">
          {t('admin.campaigns.updated', { date: formatDate(campaign.updatedAt) })}
        </p>
      )}
    </PageFrame>
  );
}

function EstimateView({ estimate }: { estimate: AudienceEstimate }) {
  const t = useT();
  const excluded = Object.entries(estimate.excluded).filter(([, n]) => n > 0);
  return (
    <div className="rounded-md border border-line bg-surface p-3 text-sm" aria-live="polite">
      <p className="font-medium">
        {t('admin.campaigns.estimateResult', { eligible: estimate.eligible, total: estimate.total })}
      </p>
      {excluded.length > 0 && (
        <ul className="mt-1 list-disc space-y-0.5 ps-5 text-ink-2">
          {excluded.map(([k, n]) => (
            <li key={k}>{t(`admin.campaigns.skipReasons.${k}`, { count: n })}</li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-ink-3">{t('admin.campaigns.estimateNote')}</p>
    </div>
  );
}

function SendButton({ campaign, dirty }: { campaign: CampaignDetail; dirty: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [later, setLater] = useState(false);
  const [at, setAt] = useState(() => toLocalInput(new Date(Date.now() + 3_600_000)));
  const [estimate, setEstimate] = useState<AudienceEstimate | null>(null);
  const idem = useRef(new IdempotencyKey());
  const est = useApiMutation(
    () =>
      api.post<AudienceEstimate>('/v1/admin/email-campaigns/audience', {
        storeId: campaign.storeId,
        audience: campaign.audience,
      }),
    { onSuccess: (r) => setEstimate(r) },
  );
  const send = useApiMutation(
    () => {
      const b = { scheduledAt: later && at ? new Date(at).toISOString() : null, version: campaign.version };
      return api.post<CampaignDetail>(`/v1/admin/email-campaigns/${campaign.id}/schedule`, b, {
        idempotencyKey: idem.current.for(JSON.stringify(b)),
      });
    },
    {
      invalidate: [['email-campaigns'], ['email-campaign', campaign.id]],
      success: later ? t('admin.campaigns.scheduledToast') : t('admin.campaigns.queuedToast'),
      silentFieldErrors: true,
      onSuccess: () => setOpen(false),
    },
  );
  const usable = campaign.template?.usable ?? false;
  return (
    <>
      <span className="inline-flex items-center gap-1">
        <Button
          variant="primary"
          disabled={dirty || !usable}
          onClick={() => {
            idem.current = new IdempotencyKey();
            send.reset();
            setEstimate(null);
            setOpen(true);
            est.mutate(undefined);
          }}
        >
          <Send className="h-4 w-4" aria-hidden />
          {t('admin.campaigns.send')}
        </Button>
        <HelpTip k="campaigns.send" />
      </span>
      {!usable && campaign.template && (
        <span className="text-sm text-ink-2">{t('admin.campaigns.templateNotApproved')}</span>
      )}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('admin.campaigns.sendTitle')}
        description={t('admin.campaigns.sendLead')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={send.isPending || !estimate?.eligible || (later && !at)}
              onClick={() => send.mutate(undefined)}
            >
              {later
                ? t('admin.campaigns.scheduleConfirm')
                : t('admin.campaigns.sendConfirm', { count: estimate?.eligible ?? 0 })}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {!estimate ? <Spinner label={t('common.loading')} /> : <EstimateView estimate={estimate} />}
          <fieldset className="space-y-1.5 text-sm">
            <legend className="mb-1 font-medium">{t('admin.campaigns.when')}</legend>
            <label className="flex items-center gap-2">
              <input type="radio" name="when" checked={!later} onChange={() => setLater(false)} />
              {t('admin.campaigns.now')}
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="when" checked={later} onChange={() => setLater(true)} />
              {t('admin.campaigns.later')}
            </label>
          </fieldset>
          {later && (
            <Field
              label={t('admin.campaigns.scheduledAt')}
              help={<HelpTip k="campaigns.scheduled_at" />}
              error={fieldError(send.error, 'scheduledAt')}
            >
              {(p) => (
                <Input {...p} type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
              )}
            </Field>
          )}
          {fieldError(send.error, 'audience') && (
            <p role="alert" className="text-sm font-medium text-rose">
              {fieldError(send.error, 'audience')}
            </p>
          )}
        </div>
      </Dialog>
    </>
  );
}

function StatusSection({
  campaign,
  canSend,
  onCancel,
  onResume,
  busy,
}: {
  campaign: CampaignDetail;
  canSend: boolean;
  onCancel: () => Promise<unknown>;
  onResume: () => void;
  busy: boolean;
}) {
  const t = useT();
  const { formatDate } = useI18n();
  const p = campaign.progress;
  const done = p ? p.total - p.queued : 0;
  return (
    <Section title={t('admin.campaigns.statusTitle')} action={<HelpTip k="campaigns.status" />}>
      <div className="space-y-3 text-sm">
        <KeyValue
          items={[
            {
              label: t('admin.campaigns.statusLabel'),
              value: t(`admin.campaigns.statuses.${campaign.status}`),
            },
            ...(campaign.scheduledAt
              ? [{ label: t('admin.campaigns.scheduledAt'), value: formatDate(campaign.scheduledAt) }]
              : []),
            ...(campaign.startedAt
              ? [{ label: t('admin.campaigns.startedAt'), value: formatDate(campaign.startedAt) }]
              : []),
            ...(campaign.status === 'testing' && campaign.abDecideAt
              ? [{ label: t('admin.campaigns.abDecideAt'), value: formatDate(campaign.abDecideAt) }]
              : []),
            ...(campaign.winner
              ? [
                  {
                    label: t('admin.campaigns.winner'),
                    value: t('admin.campaigns.variantLabel', { v: campaign.winner }),
                  },
                ]
              : []),
            ...(campaign.finishedAt
              ? [{ label: t('admin.campaigns.finishedAt'), value: formatDate(campaign.finishedAt) }]
              : []),
          ]}
        />
        {p && p.total > 0 && (
          <div className="max-w-xl space-y-1">
            <progress
              className="h-2 w-full accent-[var(--zt-pine)]"
              max={p.total}
              value={done}
              aria-label={t('admin.campaigns.progressLabel')}
            />
            <p className="text-ink-2" aria-live="polite">
              {t('admin.campaigns.progress', { done, total: p.total, sent: p.sent, skipped: p.skipped })}
            </p>
          </div>
        )}
        {campaign.abSkipped && <p className="text-ink-2">{t('admin.campaigns.abSkipped')}</p>}
        {campaign.status === 'paused' && campaign.pauseReason && (
          <p role="alert" className="rounded-md border border-rose/30 bg-rose-tint px-3 py-2 text-rose">
            {t(`admin.campaigns.pauseReasons.${campaign.pauseReason}`)}
          </p>
        )}
        {canSend && (
          <div className="flex flex-wrap items-center gap-2">
            {campaign.status === 'paused' && (
              <>
                <Button size="sm" variant="primary" onClick={onResume} disabled={busy}>
                  <Play className="h-4 w-4" aria-hidden />
                  {t('admin.campaigns.resume')}
                </Button>
                <HelpTip k="campaigns.resume" />
              </>
            )}
            {['scheduled', 'sending', 'testing', 'paused'].includes(campaign.status) && (
              <>
                <ConfirmButton
                  variant="secondary"
                  message={t('admin.campaigns.cancelConfirm')}
                  onConfirm={onCancel}
                  disabled={busy}
                >
                  <Pause className="h-4 w-4" aria-hidden />
                  {t('admin.campaigns.cancel')}
                </ConfirmButton>
                <HelpTip k="campaigns.cancel" />
              </>
            )}
          </div>
        )}
      </div>
    </Section>
  );
}

function ReportSection({ campaign }: { campaign: CampaignDetail }) {
  const t = useT();
  const money = useMoney();
  const live = LIVE.includes(campaign.status);
  const q = useQuery({
    queryKey: ['email-campaign-report', campaign.id],
    queryFn: () => api.get<CampaignReport>(`/v1/admin/email-campaigns/${campaign.id}/report`),
    refetchInterval: live ? 5000 : false,
  });
  const r = q.data;
  if (!r) return <Spinner label={t('common.loading')} />;
  const tt = r.totals;
  const stats: Array<[string, string]> = [
    ['recipients', String(tt.recipients)],
    ['sent', String(tt.sent)],
    ['skipped', String(tt.skipped)],
    ['delivered', String(tt.delivered)],
    ['bounced', String(tt.bounced)],
    ['clicked', `${tt.clicked} (%${pct(tt.clicked, tt.sent)})`],
    ['clicks', String(tt.clicks)],
    ['unsubscribed', String(tt.unsubscribed)],
    ['complained', String(tt.complained)],
    ['failed', String(tt.failed)],
  ];
  const skips = SKIP_KEYS.filter((k) => (r.skipReasons[k] ?? 0) > 0);
  return (
    <Section
      title={t('admin.campaigns.reportTitle')}
      description={t('admin.campaigns.reportLead')}
      action={<HelpTip k="campaigns.report" />}
    >
      <div className="space-y-6 text-sm">
        {r.purged && <p className="text-ink-2">{t('admin.campaigns.purged')}</p>}
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {stats.map(([k, v]) => (
            <div key={k} className="rounded-md border border-line bg-surface px-3 py-2">
              <dt className="text-ink-3">{t(`admin.campaigns.stats.${k}`)}</dt>
              <dd className="font-cond text-xl font-semibold tabular-nums">{v}</dd>
            </div>
          ))}
        </dl>
        {skips.length > 0 && (
          <div>
            <p className="mb-1 font-medium">{t('admin.campaigns.skippedTitle')}</p>
            <ul className="list-disc space-y-0.5 ps-5 text-ink-2">
              {skips.map((k) => (
                <li key={k}>{t(`admin.campaigns.skipReasons.${k}`, { count: r.skipReasons[k] ?? 0 })}</li>
              ))}
            </ul>
          </div>
        )}
        {r.variants.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full" aria-label={t('admin.campaigns.abTitle')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.campaigns.colVariant')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.campaigns.subject')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.campaigns.stats.sent')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.campaigns.stats.clicked')}</th>
                </tr>
              </thead>
              <tbody>
                {r.variants.map((v) => (
                  <tr key={v.variant} className="border-t border-line">
                    <td className="px-3 py-2">
                      {t('admin.campaigns.variantLabel', { v: v.variant })}
                      {r.winner === v.variant && (
                        <Badge tone="moss" className="ms-2">
                          {t('admin.campaigns.winnerBadge')}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2">{v.subject ?? '—'}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{v.sent}</td>
                    <td className="px-3 py-2 text-end tabular-nums">
                      {v.clicked} (%{pct(v.clicked, v.sent)})
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {r.links.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full" aria-label={t('admin.campaigns.linksTitle')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.campaigns.linksTitle')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.campaigns.stats.clicks')}</th>
                </tr>
              </thead>
              <tbody>
                {r.links.map((l) => (
                  <tr key={l.url} className="border-t border-line">
                    <td className="max-w-0 truncate px-3 py-2" title={l.url}>
                      {l.url}
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{l.clicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div>
          <p className="mb-1 font-medium">{t('admin.campaigns.ordersTitle')}</p>
          {!r.orders.length ? (
            <p className="text-ink-2">{t('admin.campaigns.noOrders')}</p>
          ) : (
            <ul className="space-y-0.5 text-ink-2">
              {r.orders.map((o) => (
                <li key={o.currency}>
                  {t('admin.campaigns.ordersLine', {
                    orders: o.orders,
                    paid: o.paidOrders,
                    revenue: money(o.revenueMinor, o.currency),
                  })}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-ink-3">{t('admin.campaigns.ordersNote')}</p>
        </div>
      </div>
    </Section>
  );
}
